// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/** Human-readable byte size. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  const rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[unit]}`;
}

/** Normalise any rotation to one of 0 / 90 / 180 / 270. Handles negatives. */
export function normalizeRotation(deg: number): number {
  const r = ((Math.round(deg / 90) * 90) % 360 + 360) % 360;
  return r;
}

/** Strip a trailing .pdf (case-insensitive) and any path separators. */
export function baseName(name: string): string {
  const noPath = name.split(/[\\/]/).pop() ?? name;
  return noPath.replace(/\.pdf$/i, '');
}

/** Make a string safe to use as a download filename. */
export function sanitizeFilename(name: string): string {
  const cleaned = name
    .replace(/[ -<>:"/\\|?*]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
  return cleaned.length ? cleaned : 'pagesmith';
}

/**
 * Derive an output filename from the source docs.
 * - one source  -> "<name> (edited).pdf"
 * - a subset    -> "<name> (pages).pdf"
 * - many        -> "<first> +N (merged).pdf"
 */
export function deriveOutputName(
  docNames: string[],
  opts: { subset?: boolean } = {},
): string {
  const names = docNames.map(baseName).filter(Boolean);
  let stem: string;
  if (names.length === 0) {
    stem = 'pagesmith';
  } else if (names.length === 1) {
    stem = opts.subset ? `${names[0]} (pages)` : `${names[0]} (edited)`;
  } else {
    stem = `${names[0]} +${names.length - 1} (merged)`;
  }
  return `${sanitizeFilename(stem)}.pdf`;
}

/** "12:04:03" style timestamp for the event log. */
export function clockStamp(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}
